import { RefObject, useCallback, useEffect, useState, useRef, useMemo } from "react";
import { useResizeObserver } from "@react-aria/utils";
import { debounce } from "lodash-es";
import { produce } from "immer";

type ScrollPosition = {
  isAtStart: boolean;
  isAtEnd: boolean;
};

const useHorizontalScrollDetection = (
  scrollContainerRef: RefObject<HTMLElement>,
  threshold: number = 1
): ScrollPosition => {
  const [scrollState, setScrollState] = useState<ScrollPosition>({
    isAtStart: true,
    isAtEnd: false,
  });

  const rafRef = useRef<number>();

  const updateScrollState = useCallback(() => {
    const container = scrollContainerRef?.current;
    if (!container) return;

    const { scrollLeft, scrollWidth, clientWidth } = container;
    const scrollRight = scrollWidth - clientWidth - scrollLeft;

    const newIsAtStart = scrollLeft <= threshold;
    const newIsAtEnd = scrollRight <= threshold;

    setScrollState((prevState) =>
      produce(prevState, (draft) => {
        draft.isAtStart = newIsAtStart;
        draft.isAtEnd = newIsAtEnd;
      })
    );
  }, [scrollContainerRef, threshold]);

  const updateScrollStateWithRAF = useCallback(() => {
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
    }

    rafRef.current = requestAnimationFrame(updateScrollState);
  }, [updateScrollState]);

  const debouncedUpdateScrollState = useMemo(
    // FYI: the debounce delay is so small because we want to apply the effect without visible delays
    () => debounce(updateScrollStateWithRAF, 8),
    [updateScrollStateWithRAF]
  );

  useResizeObserver({ ref: scrollContainerRef, onResize: updateScrollStateWithRAF });

  useEffect(() => {
    const container = scrollContainerRef?.current;
    if (!container) return;

    updateScrollStateWithRAF();

    container.addEventListener("scroll", debouncedUpdateScrollState, { passive: true });

    return () => {
      container.removeEventListener("scroll", debouncedUpdateScrollState);

      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }
      debouncedUpdateScrollState.cancel();
    };
  }, [scrollContainerRef, debouncedUpdateScrollState, updateScrollStateWithRAF]);

  return scrollState;
};

export default useHorizontalScrollDetection;
